// Lightweight anchored dropdown for the top-bar pickers.
//
// `createDropdown` wires a trigger <button> to a floating popover appended
// to document.body (so it escapes any overflow on the bar). Outside-click,
// Esc, ArrowUp/Down/Home/End, Enter/Space all behave as expected; focus
// returns to the trigger on close.
//
// The popover is rebuilt from `getOptions()` on every open (and on
// `refresh()` while open), so callers don't need to push updates — they
// just need to call `refresh()` when their data source changes.
//
// Two selection modes, picked by the discriminated `multiSelect` opt:
//   - false (default) — single-select; Enter/click commits + closes.
//   - true            — checkbox list; Enter/click toggles, popover
//                       stays open. Esc / outside-click commits the
//                       current set and closes.
// Magviz uses single-select today; multi-select is the surface webpic
// will turn on when its multi-field datasets arrive.

export interface DropdownOption {
    label: string;
    value: string;
}

interface DropdownOptsBase {
    trigger: HTMLButtonElement;
    getOptions: () => DropdownOption[];
}

export interface DropdownSingleOpts extends DropdownOptsBase {
    multiSelect?: false;
    getSelected: () => string | null;
    onSelect: (value: string) => void;
}

export interface DropdownMultiOpts extends DropdownOptsBase {
    multiSelect: true;
    getSelected: () => ReadonlyArray<string>;
    onSelect: (values: ReadonlyArray<string>) => void;
}

export type DropdownOpts = DropdownSingleOpts | DropdownMultiOpts;

export interface DropdownHandle {
    open(): void;
    close(): void;
    toggle(): void;
    refresh(): void;
    isOpen(): boolean;
    dispose(): void;
}

export function createDropdown(opts: DropdownOpts): DropdownHandle {
    const { trigger, getOptions } = opts;
    const multi = opts.multiSelect === true;
    let popover: HTMLDivElement | null = null;
    let activeIdx = -1;
    let items: HTMLElement[] = [];
    /** Mutable working set while the popover is open (multi-select only). */
    let workingSet: Set<string> = new Set();

    const isSelected = (value: string): boolean => {
        if (multi) return workingSet.has(value);
        return (opts as DropdownSingleOpts).getSelected() === value;
    };

    const seedWorkingSet = (): void => {
        if (!multi) return;
        workingSet = new Set((opts as DropdownMultiOpts).getSelected());
    };

    const ensurePopover = (): HTMLDivElement => {
        if (popover) return popover;
        const el = document.createElement('div');
        el.className = 'topbar-popover';
        el.setAttribute('role', multi ? 'listbox' : 'listbox');
        if (multi) el.setAttribute('aria-multiselectable', 'true');
        el.tabIndex = -1;
        el.style.display = 'none';
        el.addEventListener('mousedown', (e) => {
            // keep focus on whatever has it inside the popover; prevent the
            // outside-click pointerdown listener from racing the click.
            e.stopPropagation();
        });
        document.body.appendChild(el);
        popover = el;
        return el;
    };

    const positionPopover = (): void => {
        if (!popover) return;
        const rect = trigger.getBoundingClientRect();
        popover.style.left = `${Math.round(rect.left)}px`;
        popover.style.top = `${Math.round(rect.bottom + 6)}px`;
        popover.style.minWidth = `${Math.max(rect.width, 200)}px`;
    };

    const refreshSelectionMarks = (): void => {
        for (const row of items) {
            const value = row.dataset['value'] ?? '';
            const sel = isSelected(value);
            row.setAttribute('aria-selected', sel ? 'true' : 'false');
            const check = row.querySelector('.fa-check') as HTMLElement | null;
            if (check) check.classList.toggle('is-hidden', !sel);
        }
    };

    const buildItems = (): void => {
        if (!popover) return;
        const options = getOptions();
        popover.replaceChildren();
        items = options.map((opt, i) => {
            const row = document.createElement('div');
            row.className = 'topbar-popover-item';
            row.setAttribute('role', 'option');
            row.dataset['value'] = opt.value;
            const sel = isSelected(opt.value);
            row.setAttribute('aria-selected', sel ? 'true' : 'false');
            const check = document.createElement('i');
            check.className = `fas fa-check${sel ? '' : ' is-hidden'}`;
            check.setAttribute('aria-hidden', 'true');
            const label = document.createElement('span');
            label.textContent = opt.label;
            row.appendChild(check);
            row.appendChild(label);
            row.addEventListener('mouseenter', () => setActive(i));
            row.addEventListener('click', (e) => {
                e.stopPropagation();
                commit(opt.value);
            });
            popover!.appendChild(row);
            return row;
        });
        // Default highlight: the (first) selected row, else the first.
        const selIdx = items.findIndex((row) => isSelected(row.dataset['value'] ?? ''));
        setActive(selIdx >= 0 ? selIdx : 0);
    };

    const setActive = (i: number): void => {
        if (!items.length) { activeIdx = -1; return; }
        const clamped = Math.max(0, Math.min(items.length - 1, i));
        if (clamped === activeIdx) return;
        items.forEach((row, idx) => row.classList.toggle('is-active', idx === clamped));
        activeIdx = clamped;
        const row = items[clamped];
        if (row) row.scrollIntoView({ block: 'nearest' });
    };

    const commit = (value: string): void => {
        if (multi) {
            if (workingSet.has(value)) workingSet.delete(value); else workingSet.add(value);
            refreshSelectionMarks();
            return;
        }
        try {
            (opts as DropdownSingleOpts).onSelect(value);
        } catch (e) {
            console.warn('[dropdown] onSelect failed', e);
        }
        close();
    };

    const flushMulti = (): void => {
        if (!multi) return;
        const values = items
            .map((row) => row.dataset['value'] ?? '')
            .filter((v) => workingSet.has(v));
        try {
            (opts as DropdownMultiOpts).onSelect(values);
        } catch (e) {
            console.warn('[dropdown] onSelect failed', e);
        }
    };

    const onDocumentPointerDown = (e: PointerEvent): void => {
        const target = e.target as Node | null;
        if (!target) return;
        if (popover && popover.contains(target)) return;
        if (trigger.contains(target)) return;
        close();
    };

    const onKeyDown = (e: KeyboardEvent): void => {
        if (!popover) return;
        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                close();
                trigger.focus();
                break;
            case 'ArrowDown':
                e.preventDefault();
                setActive(activeIdx + 1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActive(activeIdx - 1);
                break;
            case 'Home':
                e.preventDefault();
                setActive(0);
                break;
            case 'End':
                e.preventDefault();
                setActive(items.length - 1);
                break;
            case 'Enter':
            case ' ': {
                e.preventDefault();
                const row = items[activeIdx];
                const value = row?.dataset['value'];
                if (value !== undefined) commit(value);
                break;
            }
        }
    };

    const onWindowResize = (): void => positionPopover();

    let opened = false;
    const open = (): void => {
        if (opened) return;
        seedWorkingSet();
        const el = ensurePopover();
        el.style.display = '';
        buildItems();
        positionPopover();
        opened = true;
        trigger.setAttribute('aria-expanded', 'true');
        document.addEventListener('pointerdown', onDocumentPointerDown, true);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('resize', onWindowResize);
        window.addEventListener('scroll', onWindowResize, true);
    };

    const close = (): void => {
        if (!opened) return;
        flushMulti();
        opened = false;
        trigger.setAttribute('aria-expanded', 'false');
        document.removeEventListener('pointerdown', onDocumentPointerDown, true);
        document.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('resize', onWindowResize);
        window.removeEventListener('scroll', onWindowResize, true);
        if (popover) {
            popover.replaceChildren();
            popover.style.display = 'none';
        }
        items = [];
        activeIdx = -1;
    };

    const onTriggerClick = (e: MouseEvent): void => {
        e.stopPropagation();
        if (opened) close(); else open();
    };
    const onTriggerKeyDown = (e: KeyboardEvent): void => {
        if (opened) return;
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
        }
    };

    trigger.addEventListener('click', onTriggerClick);
    trigger.addEventListener('keydown', onTriggerKeyDown);

    return {
        open,
        close,
        toggle: () => { if (opened) close(); else open(); },
        refresh: () => { if (opened) { buildItems(); positionPopover(); } },
        isOpen: () => opened,
        dispose: () => {
            close();
            trigger.removeEventListener('click', onTriggerClick);
            trigger.removeEventListener('keydown', onTriggerKeyDown);
            if (popover && popover.parentNode) popover.parentNode.removeChild(popover);
            popover = null;
        },
    };
}
